import React, { useState } from "react";
import Sidebar from "../../../components/Sidebar/Sidebar";
import AdminList from "../../../components/AdminList/AdminList";
import Modal from "../../../components/Modal/Modal";
import { getBlogs, deleteBlog, createBlog, updateBlog } from "../../../utils/api";

const columns = [
  { key: "title", label: "Title" },
  { key: "author", label: "Author" },
  {
    key: "shortDescription",
    label: "Summary",
    render: (value) => value ? String(value).slice(0, 120) : "-"
  },
  { 
    key: "publishDate", 
    label: "Publish Date",
    render: (value) => value ? new Date(value).toLocaleDateString() : "-"
  },
];

const filters = [
  {
    key: "author",
    label: "Filter by Author",
    type: "search"
  },
];

const initialFormData = {
  title: "",
  shortDescription: "",
  content: "",
  author: "Maha Sabha",
  imageUrl: "",
  imageUrlsText: "",
  videoUrlsText: "",
  linkTitle: "",
  linkUrl: "",
};

const splitLines = (raw = "") =>
  String(raw)
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

export default function Blogs() {
  const [isModalOpen, setModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedBlog, setSelectedBlog] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [loading, setLoading] = useState(false);

  const handleAdd = () => {
    setFormData(initialFormData);
    setIsEditMode(false);
    setSelectedBlog(null);
    setModalOpen(true);
  };

  const handleEdit = (blog) => {
    const mediaItems = Array.isArray(blog.mediaItems) ? blog.mediaItems : [];
    const imageUrlsText = mediaItems
      .filter((item) => item?.type === "image" && item?.url)
      .map((item) => item.url)
      .join("\n");
    const videoUrlsText = mediaItems
      .filter((item) => item?.type === "video" && item?.url)
      .map((item) => item.url)
      .join("\n");
    const primaryLink = Array.isArray(blog.links) && blog.links.length > 0 ? blog.links[0] : null;

    setSelectedBlog(blog);
    setFormData({
      title: blog.title || "",
      shortDescription: blog.shortDescription || "",
      content: blog.content || "",
      author: blog.author || "Maha Sabha",
      imageUrl: blog.imageUrl || "",
      imageUrlsText,
      videoUrlsText,
      linkTitle: primaryLink?.title || "",
      linkUrl: primaryLink?.url || "",
    });
    setIsEditMode(true);
    setModalOpen(true);
  };

  const handleView = (blog) => {
    handleEdit(blog);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          const maxSize = 500;
          let width = img.width;
          let height = img.height;
          
          if (width > height) {
            if (width > maxSize) {
              height = (height * maxSize) / width;
              width = maxSize;
            }
          } else {
            if (height > maxSize) {
              width = (width * maxSize) / height;
              height = maxSize;
            }
          }
          
          canvas.width = width;
          canvas.height = height;
          ctx.drawImage(img, 0, 0, width, height);
          const compressedBase64 = canvas.toDataURL('image/jpeg', 0.5);
          
          setFormData(prev => ({ ...prev, imageUrl: compressedBase64 }));
        };
        img.src = reader.result;
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const payload = {
      title: formData.title,
      shortDescription: formData.shortDescription,
      content: formData.content,
      author: formData.author,
      imageUrl: formData.imageUrl,
      imageUrls: splitLines(formData.imageUrlsText),
      videoUrls: splitLines(formData.videoUrlsText),
      linkTitle: formData.linkTitle,
      linkUrl: formData.linkUrl,
    };

    try {
      if (isEditMode && selectedBlog) {
        await updateBlog(selectedBlog._id, payload);
        alert("Blog updated successfully!");
      } else {
        await createBlog(payload);
        alert("Blog created successfully!");
      }
      setModalOpen(false);
    } catch (error) {
      alert(error.message || "Failed to save blog");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="dashboard-container">
      <Sidebar />
      <main className="dashboard-content">
        <AdminList
          title="Blogs"
          fetchData={getBlogs}
          deleteData={deleteBlog}
          columns={columns}
          onEdit={handleEdit}
          onAdd={handleAdd}
          onView={handleView}
          filters={filters}
          searchPlaceholder="Search blogs..."
        />
      </main>

      {/* Create/Edit Blog Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setModalOpen(false)}
        title={isEditMode ? "Edit Blog" : "Add New Blog"}
        size="large"
      >
        <form onSubmit={handleSubmit}>
          <div className="form-section">
            <h3>Basic Information</h3>
            <div className="form-group">
              <label>Title *</label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleFormChange}
                required
                placeholder="Enter blog title"
              />
            </div>
            <div className="form-group">
              <label>Short Description * (max 300)</label>
              <textarea
                name="shortDescription"
                value={formData.shortDescription}
                onChange={handleFormChange}
                rows="3"
                maxLength={300}
                required
                placeholder="Short summary for cards and notifications"
              />
            </div>
            <div className="form-group">
              <label>Author</label>
              <input
                type="text"
                name="author"
                value={formData.author}
                onChange={handleFormChange}
                placeholder="Enter author name"
              />
            </div>
          </div>

          <div className="form-section">
            <h3>Content</h3>
            <div className="form-group">
              <label>Content *</label>
              <textarea
                name="content"
                value={formData.content}
                onChange={handleFormChange}
                rows="10"
                required
                placeholder="Enter blog content"
              />
            </div>
          </div>

          <div className="form-section">
            <h3>Image</h3>
            <div className="form-group">
              <label>Cover Image</label>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                className="file-input"
              />
              {formData.imageUrl && (
                <div className="image-preview">
                  <img src={formData.imageUrl} alt="Preview" style={{ maxWidth: '100px', maxHeight: '100px' }} />
                  <button type="button" onClick={() => setFormData(prev => ({ ...prev, imageUrl: "" }))}>
                    Remove
                  </button>
                </div>
              )}
            </div>
            <div className="form-group">
              <label>Extra Image URLs (one per line)</label>
              <textarea
                name="imageUrlsText"
                value={formData.imageUrlsText}
                onChange={handleFormChange}
                rows="3"
                placeholder={"https://image-1.jpg\nhttps://image-2.jpg"}
              />
            </div>
            <div className="form-group">
              <label>Video URLs (one per line)</label>
              <textarea
                name="videoUrlsText"
                value={formData.videoUrlsText}
                onChange={handleFormChange}
                rows="3"
                placeholder="https://video-1.mp4"
              />
            </div>
          </div>

          <div className="form-section">
            <h3>Optional Link</h3>
            <div className="form-row">
              <div className="form-group">
                <label>Link Title</label>
                <input
                  type="text"
                  name="linkTitle"
                  value={formData.linkTitle}
                  onChange={handleFormChange}
                  placeholder="Read more"
                />
              </div>
              <div className="form-group">
                <label>Link URL</label>
                <input
                  type="url"
                  name="linkUrl"
                  value={formData.linkUrl}
                  onChange={handleFormChange}
                  placeholder="https://..."
                />
              </div>
            </div>
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setModalOpen(false)}
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? "Saving..." : (isEditMode ? "Update Blog" : "Create Blog")}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
